import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  TransactionHeader,
  TransactionNotes,
  TransactionDetailsCard,
  SwapCurrencyCards,
  TransactionStatusProgress,
  TransactionInfoCard,
  SwapStatus,
  copyToClipboard,
  formatDate,
  getSwapSteps,
  getSwapStepState,
  getSwapProgressValue,
  Button,
  LoadingPage,
  pendingFilter,
  canApproveTransaction,
  Badge,
  CompanyRoleBadge,
  FlaggedBadgeIcon,
  ApproveConfirmationDialog,
  RejectConfirmationDialog,
  TransactionError,
  FallbackGroupIcon,
  FallbackCompanyIcon,
  buildSwapSummaryDisplay,
  parseFormattedAmount,
} from '@valiant/ui-shared'
import { Check, X, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  useGetSwapTransactionQuery,
  useApproveSwapMutation,
  useRejectSwapMutation,
  useFlagSwapMutation,
  useCreateSwapNoteMutation,
  useGetCompanyQuery,
} from '../generated/graphql'
import { useAuth } from '@/AuthProvider'
import { MainLayout } from '@/components/layout/MainLayout'

export function SwapTransaction() {
  const { transactionId } = useParams()
  const navigate = useNavigate()
  const { auth } = useAuth()
  const isComplianceUser = auth?.role === 'COMPLIANCE'
  const [, setCopiedField] = useState<string | null>(null)
  const [isFlagged, setIsFlagged] = useState(false)
  const [newNote, setNewNote] = useState('')
  const [status, setStatus] = useState<SwapStatus>(SwapStatus.Requested)
  const [showApproveDialog, setShowApproveDialog] = useState(false)
  const [showRejectDialog, setShowRejectDialog] = useState(false)
  const [isApproving, setIsApproving] = useState(false)
  const [isRejecting, setIsRejecting] = useState(false)
  const [isFlagging, setIsFlagging] = useState(false)
  const [isSendingNote, setIsSendingNote] = useState(false)
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false)

  const canShowActions = canApproveTransaction(status, isComplianceUser, 'TRADE')

  // GraphQL query for swap transaction
  const [{ data: swapData, fetching: isLoading, error }, refetch] = useGetSwapTransactionQuery({
    variables: { id: transactionId || '' },
    pause: !transactionId,
  })

  const [{ data: companyData }] = useGetCompanyQuery({
    variables: { companyId: swapData?.getSwap?.companyId || '' },
    pause: !swapData?.getSwap?.companyId,
  })

  const swapTransaction = swapData?.getSwap

  // GraphQL mutations
  const [, approveSwapMutation] = useApproveSwapMutation()
  const [, rejectSwapMutation] = useRejectSwapMutation()
  const [, flagSwapMutation] = useFlagSwapMutation()
  const [, createSwapNoteMutation] = useCreateSwapNoteMutation()

  // Set initial state from GraphQL data
  useEffect(() => {
    if (swapTransaction) {
      setStatus(swapTransaction.status)
      setIsFlagged(!!swapTransaction.isFlagged)
    }
  }, [swapTransaction])

  // Track when initial data has been loaded
  useEffect(() => {
    if (swapTransaction && !hasInitiallyLoaded) {
      setHasInitiallyLoaded(true)
    }
  }, [swapTransaction, hasInitiallyLoaded])

  // Ref to store the interval ID for cleanup
  const refetchIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Auto-refetch for non-finalized swaps (excluding requested state)
  useEffect(() => {
    // Clear any existing interval
    if (refetchIntervalRef.current) {
      clearInterval(refetchIntervalRef.current)
      refetchIntervalRef.current = null
    }

    // Check if we should refetch based on status
    const shouldRefetch = swapTransaction && status && pendingFilter.includes(status)

    if (shouldRefetch) {
      // Refetch every 5 seconds for active/processing states
      refetchIntervalRef.current = setInterval(() => {
        refetch({ requestPolicy: 'network-only' })
      }, 5000)
    }

    // Cleanup function
    return () => {
      if (refetchIntervalRef.current) {
        clearInterval(refetchIntervalRef.current)
        refetchIntervalRef.current = null
      }
    }
  }, [status, swapTransaction, refetch])

  const handleCopy = async (text: string, field: string) => {
    const success = await copyToClipboard(text)
    if (success) {
      setCopiedField(field)
      setTimeout(() => setCopiedField(null), 2000)
      toast.success('Copied to clipboard')
    }
  }

  const handleApprove = () => {
    setShowApproveDialog(true)
  }

  const handleReject = () => {
    setShowRejectDialog(true)
  }

  const handleFlagToggle = async (flagged: boolean) => {
    if (!transactionId || isFlagging) return

    setIsFlagging(true)
    try {
      const result = await flagSwapMutation({
        input: { swapId: transactionId, flagged },
      })

      if (result.error) {
        throw new Error(result.error.message)
      }

      toast.success(`Transaction ${flagged ? 'flagged' : 'unflagged'} successfully`)
      setIsFlagged(flagged)
    } catch (error) {
      toast.error(`Failed to ${flagged ? 'flag' : 'unflag'} transaction`)
      console.error('Error flagging transaction:', error)
      setIsFlagged(!flagged)
    } finally {
      setIsFlagging(false)
    }
  }

  const confirmApprove = useCallback(async () => {
    if (!transactionId || isApproving) return

    setIsApproving(true)
    try {
      const result = await approveSwapMutation({
        input: { swapId: transactionId },
      })

      if (result.error) {
        throw new Error(result.error.message)
      }

      toast.success('Transaction approved successfully')
      setStatus(SwapStatus.Approved)
      setShowApproveDialog(false)
    } catch (error) {
      toast.error('Failed to approve transaction')
      console.error('Error approving transaction:', error)
    } finally {
      setIsApproving(false)
    }
  }, [approveSwapMutation, transactionId, isApproving])

  const confirmReject = useCallback(async () => {
    if (!transactionId || isRejecting) return

    setIsRejecting(true)
    try {
      const result = await rejectSwapMutation({
        input: { swapId: transactionId },
      })

      if (result.error) {
        throw new Error(result.error.message)
      }

      toast.success('Transaction rejected successfully')
      setStatus(SwapStatus.Rejected)
      setShowRejectDialog(false)
    } catch (error) {
      toast.error('Failed to reject transaction')
      console.error('Error rejecting transaction:', error)
    } finally {
      setIsRejecting(false)
    }
  }, [rejectSwapMutation, transactionId, isRejecting])

  const handleSendNote = async () => {
    if (!newNote.trim() || !transactionId || isSendingNote) return

    setIsSendingNote(true)
    try {
      const result = await createSwapNoteMutation({
        input: {
          swapId: transactionId,
          content: newNote.trim(),
        },
      })

      if (result.error) {
        throw new Error(result.error.message)
      }

      toast.success('Note added successfully')
      setNewNote('')
    } catch (error) {
      toast.error('Failed to add note')
      console.error('Error creating note:', error)
    } finally {
      setIsSendingNote(false)
    }
  }

  const modalsComponent = useMemo(() => {
    return (
      <>
        <ApproveConfirmationDialog
          open={showApproveDialog}
          onOpenChange={setShowApproveDialog}
          onConfirm={confirmApprove}
          isSubmitting={isApproving}
          title="Approve Transaction"
          description="Are you sure you want to approve this swap transaction? This action cannot be undone."
        />
        <RejectConfirmationDialog
          open={showRejectDialog}
          onOpenChange={setShowRejectDialog}
          onConfirm={confirmReject}
          isSubmitting={isRejecting}
          title="Reject Transaction"
          description="Are you sure you want to reject this swap transaction? This action cannot be undone."
        />
      </>
    )
  }, [showApproveDialog, showRejectDialog, isApproving, isRejecting, confirmApprove, confirmReject])

  const badges = useMemo(() => {
    return [
      {
        node: <CompanyRoleBadge tradeType={companyData?.getCompany?.tradeType || ''} />,
        direction: 'LEFT',
      },
      {
        node: (
          <Badge variant="outline" className="text-xs ">
            {swapTransaction?.group}
          </Badge>
        ),
        direction: 'LEFT',
      },
    ]
  }, [companyData, swapTransaction])

  // Loading state - only show on first load
  if (isLoading && !hasInitiallyLoaded) {
    return <LoadingPage />
  }

  // Error state
  if (error) {
    return <TransactionError message={error?.message || ''} />
  }

  // Not found state
  if (!swapTransaction) {
    return (
      <TransactionError
        message="The swap transaction you're looking for doesn't exist."
        title="Transaction Not Found"
      />
    )
  }

  return (
    <MainLayout
      breadcrumbs={[
        {
          label: swapTransaction.group,
          onClick: () => navigate(`/groups/${swapTransaction.groupId}`),
          imageUrl: swapTransaction.groupImageUrl || '',
          fallbackImage: <FallbackGroupIcon />,
        },
        {
          label: swapTransaction.company,
          onClick: () => navigate(`/companies/${swapTransaction.companyId}`),
          imageUrl: swapTransaction.companyImageUrl || '',
          fallbackImage: <FallbackCompanyIcon />,
        },
      ]}
      badges={badges}
      modalsComponent={modalsComponent}
      subBarOverride={
        <TransactionHeader
          transactionId={swapTransaction.id}
          status={status}
          transactionType="TRADE"
          showActions={!isComplianceUser}
          onApprove={handleApprove}
          onReject={handleReject}
          isApproving={isApproving}
          isRejecting={isRejecting}
          isFlagged={isFlagged}
          isExternalApi={swapTransaction.isExternalApi}
        />
      }
    >
      {/* Transaction Overview Header */}
      <div className="space-y-4 mb-6 flex items-center justify-between md:block">
        <h2 className="hidden md:block text-xl font-semibold">Transaction Overview</h2>
        <h3 className="text-2xl md:text-4xl font-normal md:font-semibold md:text-main flex gap-2 items-center mb-0">
          <div className="md:hidden">{isFlagged ? <FlaggedBadgeIcon /> : null}</div>
          Swap
        </h3>
        {canShowActions && (
          <div className="flex gap-2 md:hidden">
            <Button
              onClick={handleApprove}
              variant="main_action"
              className="bg-main/95 hover:bg-main h-9 rounded-sm"
              disabled={isApproving}
            >
              {isApproving ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-1" />
              )}
              {isApproving ? 'Approving...' : 'Approve'}
            </Button>
            <Button variant="outline" onClick={handleReject} className="h-9" disabled={isRejecting}>
              {isRejecting ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <X className="w-4 h-4 mr-1" />
              )}
              {isRejecting ? 'Rejecting...' : 'Reject'}
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 lg:grid-cols-3 gap-6">
        {/* Column 1 - From/To Cards and Transaction Status */}
        <div className="space-y-6 md:col-span-2 xl:col-span-2">
          <SwapCurrencyCards
            fromMint={{
              ...swapTransaction.fromMint,
              address: swapTransaction.fromMint.address || '',
              image: swapTransaction.fromMint.image || undefined,
            }}
            toMint={{
              ...swapTransaction.toMint,
              address: swapTransaction.toMint.address || '',
              image: swapTransaction.toMint.image || undefined,
            }}
            fromAmount={swapTransaction.fromAmount}
            toAmount={swapTransaction.toAmount}
            status={status}
          />
          <TransactionStatusProgress
            status={status}
            steps={getSwapSteps()}
            getStepState={(index) => getSwapStepState(index, status)}
            getProgressValue={getSwapProgressValue}
          />
        </div>

        {/* Column 2 - Info and Details */}
        <div className="space-y-6 md:col-span-2 xl:col-span-1">
          <TransactionInfoCard
            user={swapTransaction.requestedBy || '-'}
            company={swapTransaction.company}
            group={swapTransaction.group}
            decisionBy={swapTransaction.decisionBy || '-'}
            transactionId={swapTransaction.id}
            creationDate={formatDate(swapTransaction.createdAt)}
            decisionDate={swapTransaction.decisionAt ? formatDate(swapTransaction.decisionAt) : '-'}
            finalizedDate={
              swapTransaction.completedAt ? formatDate(swapTransaction.completedAt) : '-'
            }
            onCopy={handleCopy}
            swapSummary={buildSwapSummaryDisplay({
              fromAmount: parseFormattedAmount(swapTransaction.fromAmount),
              toAmount: parseFormattedAmount(swapTransaction.toAmount),
              swapGroupBps: swapTransaction.swapGroupBps,
              swapValiantBps: swapTransaction.swapValiantBps,
              fromDecimals: swapTransaction.fromMint.displayDecimals ?? 6,
              toDecimals: swapTransaction.toMint.displayDecimals ?? 6,
              fromSymbol: swapTransaction.fromMint.symbol,
              toSymbol: swapTransaction.toMint.symbol,
            })}
          />

          <TransactionDetailsCard
            title="Details"
            fields={[
              {
                label: 'Withdrawal Transaction Hash',
                value: swapTransaction.pushoutWithdrawalTxHash || '-',
                fieldKey: 'hash',
              },
              {
                label: 'Withdrawal Address',
                value: swapTransaction.whitelistedAddress,
                fieldKey: 'address',
              },
            ]}
            onCopy={handleCopy}
            chain={swapTransaction.toMint.chain}
          />
        </div>

        {/* Column 3 - Notes */}
        <div className="md:col-span-2 xl:col-span-1">
          <TransactionNotes
            notes={swapTransaction.notes || []}
            newNote={newNote}
            onNewNoteChange={setNewNote}
            onSendNote={handleSendNote}
            isSendingNote={isSendingNote}
            isFlagged={isFlagged}
            onFlagToggle={handleFlagToggle}
            isFlagging={isFlagging}
            showFlagToggle={true}
          />
        </div>
      </div>
    </MainLayout>
  )
}
